import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listNotifications } from '../api/notifications';
import { Notification } from '../types/notification';
import { getPopupsEnabled, getSoundEnabled } from '../config/notificationPreferences';
import { playNotificationChime } from '../config/notificationSound';
import { invalidateFinanceData, PAYMENT_NOTIFICATION_TYPES } from '../utils/financeQueries';
import { useNotificationSummary } from './useNotificationSummary';

const WATCH_PAGE_SIZE = 10;

/** Real-time notification system (Phase 51) §16 — this codebase's real-
 * time mechanism for notifications was already short-interval React
 * Query polling before this phase (see the pre-existing bell/sidebar
 * hooks' own doc comments); rather than introduce a WebSocket/SSE
 * transport and the nginx/proxy-buffering changes that would require,
 * this tightens the existing mechanism (60s/30s -> 8s) and adds a
 * client-side "has this id already been shown as a toast" diff, so a
 * toast appears within one poll cycle of the notification actually being
 * created — no page refresh needed (§2), while every server endpoint,
 * schema, and permission model stays exactly as it already was.
 *
 * The first successful fetch never produces toasts (it seeds the
 * "already seen" set) — otherwise every page load/refresh would replay
 * a toast for every already-existing unread notification, which is
 * exactly the "don't spam" (§27) and "refresh keeps state correct, not
 * re-announced" (Test 10) requirements this must satisfy.
 *
 * Notification 429 investigation (2026-11-03): this no longer polls on
 * its own (it fetched the unread list every 8s in every tab). The shared
 * notification summary (one 60s poll) carries the newest unread id; the
 * list is fetched only when that changes — i.e. when there is actually
 * something new to show.
 */
export const useNotificationWatcher = () => {
  const queryClient = useQueryClient();
  const seenIds = useRef<Set<number> | null>(null);
  const [toasts, setToasts] = useState<Notification[]>([]);

  const summary = useNotificationSummary();
  const latestUnreadId = summary.data?.latestUnreadId;

  // Nothing unread when the app opened: there is no list to seed from, so
  // the first notification that arrives is new.
  useEffect(() => {
    if (summary.data && latestUnreadId == null && seenIds.current === null)
      seenIds.current = new Set();
  }, [summary.data, latestUnreadId]);

  const query = useQuery({
    queryKey: ['notifications-watch-unread', latestUnreadId],
    queryFn: () => listNotifications({ page: 1, pageSize: WATCH_PAGE_SIZE, unreadOnly: true }),
    enabled: latestUnreadId != null,
    staleTime: Infinity,
  });

  useEffect(() => {
    // Guards against more than just `undefined` — a malformed response
    // (e.g. an unexpected empty body) that still resolves successfully
    // must never reach `.map`/`.filter` on a non-array (see
    // `listNotifications`'s own guard against this same class of
    // response, which should make this unreachable in practice, but this
    // hook has no visibility into that, so it defends independently).
    const notifications = query.data?.data;
    if (!Array.isArray(notifications)) return;
    const ids = notifications.map((n) => n.id);

    if (seenIds.current === null) {
      seenIds.current = new Set(ids);
      return;
    }

    const fresh = notifications.filter((n) => !seenIds.current!.has(n.id));
    if (fresh.length === 0) return;

    fresh.forEach((n) => seenIds.current!.add(n.id));

    if (getPopupsEnabled()) {
      setToasts((prev) => [...fresh, ...prev]);
    }
    if (getSoundEnabled()) {
      playNotificationChime();
    }

    // (The bell and sidebar badges come from the same summary that
    // announced this, so they're already up to date.)
    // Phase 102: a payment was submitted or decided elsewhere — the Finance
    // Dashboard and review queue on screen are stale too.
    if (fresh.some((n) => (PAYMENT_NOTIFICATION_TYPES as readonly string[]).includes(n.type))) {
      invalidateFinanceData(queryClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const dismiss = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  // Checks for something new now (re-reads the summary, which fetches the list if needed).
  return { toasts, dismiss, refetch: summary.refetch };
};
